import React, { useMemo, useCallback } from 'react';
import { TagBlock, MenuItemProps } from '@televet/kibble-ui';
import useClinicUser from 'shared/hooks/useClinicUser';
import { useGetClinicUsersQuery } from 'shared/types/graphql';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';

interface AssigneesSelectorProps {
  selectedAssigneeIds: string[];
  setSelectedAssigneeIds: (ids: string[]) => void;
}

const AssigneesSelector: React.FC<AssigneesSelectorProps> = ({ selectedAssigneeIds, setSelectedAssigneeIds }) => {
  const { currentClinicId } = useClinicUser();

  const { data: usersData } = useGetClinicUsersQuery({
    variables: { clinicId: currentClinicId || '' },
    skip: !currentClinicId,
    fetchPolicy: GraphQLFetchPolicies.CacheFirst,
  });

  const menuItems = useMemo(
    () =>
      (usersData?.users || []).map((user) => ({
        value: user.id,
        label: `${user.firstName} ${user.lastName}`,
        isSelected: selectedAssigneeIds.includes(user.id),
      })),
    [usersData?.users, selectedAssigneeIds],
  );

  const selectedAssigneesTags = useMemo(
    () =>
      selectedAssigneeIds.map((id) => {
        const user = usersData?.users?.find((u) => u.id === id);
        return {
          label: user ? `${user.firstName} ${user.lastName}` : '(Inactive user)',
          value: id,
        };
      }),
    [selectedAssigneeIds, usersData?.users],
  );

  const handleAllSelection = useCallback(
    (selectedItems: MenuItemProps[]): void => {
      setSelectedAssigneeIds(selectedItems.map((i) => i.value || ''));
    },
    [setSelectedAssigneeIds],
  );

  const handleSelection = useCallback(
    (selectedItem: MenuItemProps, selectedItems?: MenuItemProps[]): void => {
      handleAllSelection(selectedItems || []);
    },
    [handleAllSelection],
  );

  const handleRemove = useCallback(
    (value: string): void => {
      setSelectedAssigneeIds(selectedAssigneeIds.filter((id) => id !== value));
    },
    [setSelectedAssigneeIds, selectedAssigneeIds],
  );

  return (
    <TagBlock
      dataTestId="assignees-tag-block"
      title="Assignees"
      tags={selectedAssigneesTags}
      menuContainerProps={{
        strategy: 'fixed',
        placement: 'top-start',
      }}
      menuListProps={{
        isMultiSelect: true,
        menuItems,
        onSelect: handleSelection,
        isSearchable: true,
        canSelectAll: true,
        onSelectAll: handleAllSelection,
      }}
      onClearAll={(): void => handleAllSelection([])}
      onRemoveTag={handleRemove}
    />
  );
};

export default AssigneesSelector;
